import { Button } from "@procurement-agents/ui";
import type { IntakeReviewPayload, IntakeReviewResume, ReviewItem } from "@procurement-agents/scope-api";
import { useState } from "react";
import { AutoTextarea } from "./AutoTextarea";

const PROVENANCE: Record<string, string> = {
  extracted: "from your description",
  confirmed: "from your description",
  corrected_at_review: "you changed this",
  form: "you filled this in",
  skipped: "not answered",
  to_confirm: "you didn't know yet",
};

/** CHANGE_intake_review: everything the intake gathered, before the requisition is written. The card is built by
 *  the server from what was recorded — nothing here is a model's summary — so what the requester checks is what
 *  the agent will actually use. */
export function IntakeReview({ payload, onSubmit, busy }: { payload: IntakeReviewPayload; onSubmit: (reply: IntakeReviewResume) => void; busy: boolean }) {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const changed = Object.keys(edits);
  const notAsked = payload.not_asked ?? [];

  const send = () => {
    if (!changed.length) return onSubmit({ confirm: true });
    onSubmit({ corrections: Object.fromEntries(changed.map((id) => [id, edits[id]])) });
  };

  const row = (item: ReviewItem, editable: boolean) => {
    const pending = edits[item.question_id];
    return (
      <div key={item.question_id} className={`review-row${pending !== undefined ? " edited" : ""}`}>
        <div className="review-label">{item.label}</div>
        <div className="review-value">
          {open === item.question_id ? (
            <Editor item={item} value={pending ?? String(item.raw ?? "")} onCancel={() => setOpen(null)}
              onDone={(v) => { setEdits((e) => v.trim() === "" && item.raw == null ? e : { ...e, [item.question_id]: v }); setOpen(null); }} />
          ) : (
            <>
              <span>{pending ?? item.value}</span>
              {item.provenance && PROVENANCE[item.provenance] && <span className="muted small"> · {PROVENANCE[item.provenance]}</span>}
              {editable && <button type="button" className="link small" disabled={busy} onClick={() => setOpen(item.question_id)} aria-label={`Change ${item.label}`}>Change</button>}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="turn agent">
      <p>{payload.prompt}</p>
      <div className="card review-card">
        <h4>{payload.heading}</h4>
        <div className="review-rows">{payload.items.map((i) => row(i, i.editable !== false))}</div>

        {notAsked.length > 0 && (
          <details className="review-skipped" open={showSkipped} onToggle={(e) => setShowSkipped((e.target as HTMLDetailsElement).open)}>
            <summary className="small muted">Not asked ({notAsked.length})</summary>
            <p className="muted small">Questions this request didn&apos;t need. They are here so nothing looks missing.</p>
            <div className="review-rows">{notAsked.map((i) => row(i, false))}</div>
          </details>
        )}

        <div className="row gap end review-actions">
          {changed.length > 0 && <button type="button" className="link" disabled={busy} onClick={() => setEdits({})}>Undo changes</button>}
          <Button variant="primary" disabled={busy} onClick={send}>
            {changed.length ? `Send ${changed.length} change${changed.length === 1 ? "" : "s"}` : "Looks right"}
          </Button>
        </div>
        <p className="muted small">Or type a correction below — &ldquo;the budget is actually 3.8m&rdquo; works too.</p>
      </div>
    </div>
  );
}

/** The control matches the answer's own type, so a correction is entered the way the question was asked. */
function Editor({ item, value, onDone, onCancel }: { item: ReviewItem; value: string; onDone: (v: string) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState(value);
  const commit = () => onDone(draft);
  if (item.answer_type === "choice" && item.choices?.length) {
    return (
      <select autoFocus value={draft} onChange={(e) => onDone(e.target.value)} onBlur={onCancel} aria-label={item.label}>
        <option value="">—</option>
        {item.choices.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    );
  }
  return (
    // the whole answer, in full, to edit: Enter saves, Shift+Enter starts a new line
    <span className="review-edit">
      <AutoTextarea autoFocus value={draft} aria-label={item.label}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); } if (e.key === "Escape") onCancel(); }} />
      <span className="row gap">
        <button type="button" className="link" onClick={commit}>Done</button>
        <button type="button" className="link muted" onClick={onCancel}>Cancel</button>
      </span>
    </span>
  );
}
